/**
 * Online store, session C — is a product on the shop's website, and why not.
 *
 * The rule itself lives in the database (`public.store_listed`, 153) and is
 * what the website and an order obey; the till repeats its branches here only
 * to say, beside a product's „საიტზე" switch, why a product is not shown. The
 * numbers the owner sees („საიტზე გამოჩნდება 214") come from the database
 * (`store_listing_count`), never from this copy.
 *
 * Two ways a shop lists (store_sites.listing), never mixed (Codex's review):
 * „by hand" — only what it switched on (`online`); „automatic" — every
 * product with a photo, unless it hid one (`online_hidden`). A price of 0 is
 * never shown.
 */

export type Listing = 'manual' | 'auto'
export type ListingReason = 'off' | 'hidden' | 'noPhoto' | 'noPrice' | null

type Listed = { online?: boolean | null; online_hidden?: boolean | null; photo_url?: string | null; sale_price: number }

export function listingState(p: Listed, listing: Listing): { shown: boolean; reason: ListingReason } {
  if (!(p.sale_price > 0)) return { shown: false, reason: 'noPrice' }
  if (listing === 'manual') return p.online ? { shown: true, reason: null } : { shown: false, reason: 'off' }
  if (p.online_hidden) return { shown: false, reason: 'hidden' }
  return (p.photo_url ?? '').trim() ? { shown: true, reason: null } : { shown: false, reason: 'noPhoto' }
}

/**
 * What the „საიტზე" switch writes: by hand `online`, automatic `online_hidden`
 * — each mode its own mark only, so changing the shop's mode later never
 * finds the other mark rewritten.
 */
export function switchPatch(on: boolean, listing: Listing): { online: boolean } | { online_hidden: boolean } {
  return listing === 'manual' ? { online: on } : { online_hidden: !on }
}

/**
 * The marks a model's NEW sizes take when they are made (Codex, 153): the
 * switch's when it was touched; else the model's own when every size it has
 * agrees — all on by hand, all hidden automatically — so a size added later
 * neither drops off the site nor slips onto it; else the table's defaults.
 * `sizesOn` counts the sizes carrying the mode's „on" mark.
 */
export function newSizeMarks(listing: Listing, touched: boolean | null, sizes: number, sizesOn: number): { online?: boolean; online_hidden?: boolean } {
  if (touched !== null) return switchPatch(touched, listing)
  if (sizes === 0) return {}
  if (listing === 'manual') return sizesOn === sizes ? { online: true } : {}
  return sizesOn === 0 ? { online_hidden: true } : {}
}

/** The shop's website as the till reads it (store_sites, 149/150/153). The
 *  address and on/off are the platform admin's; the rest is the owner's. */
export type StoreSite = {
  slug: string
  enabled: boolean
  listing: Listing
  tagline: string | null
  hero_url: string | null
  phone: string | null
  delivery_fee: number | null
  free_delivery_from: number | null
}

/** What the owner may change (update_store_site, 153). */
export type StoreSitePatch = Pick<StoreSite, 'listing' | 'tagline' | 'hero_url' | 'phone' | 'delivery_fee' | 'free_delivery_from'>

const moneyOrNull = (v: unknown) => (v == null || v === '' ? null : Math.round(Number(v) * 100) / 100)

export function mapStoreSite(r: any): StoreSite | null {
  if (!r || typeof r.slug !== 'string') return null
  return {
    slug: r.slug,
    enabled: r.enabled === true,
    listing: r.listing === 'auto' ? 'auto' : 'manual',
    tagline: r.tagline ?? null,
    hero_url: r.hero_url ?? null,
    phone: r.phone ?? null,
    delivery_fee: moneyOrNull(r.delivery_fee),
    free_delivery_from: moneyOrNull(r.free_delivery_from),
  }
}

/** The public address of a store. */
export const storeUrl = (slug: string) => `https://${slug}.martepos.com`
